import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import './App.css';
import './i18n';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { IrrigationProvider } from './contexts/IrrigationContext';
import { CgmDisplayModeProvider } from './contexts/CgmDisplayModeContext';
import CgmExpertRoute from './components/CgmExpertRoute';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AgriSafeFatR1 from './pages/AgriSafeFatR1';
import Map from './pages/Map';
import Zones from './pages/Zones';
import Devices from './pages/Devices';
import Alerts from './pages/Alerts';
import Schedules from './pages/Schedules';
import IrrigationHistory from './pages/IrrigationHistory';
import CgmIrrigationJournal from './pages/cgm/CgmIrrigationJournal';
import ProgrammingPage from './irrigation-programming/ProgrammingPage';
import IoTNodes from './pages/IoTNodes';
import WorkOrders from './pages/WorkOrders';
import Settings from './pages/Settings';
import OrgSwitch from './pages/OrgSwitch';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminDashboardAws from './pages/admin/AdminDashboardAws';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUsersAws from './pages/admin/AdminUsersAws';
import AdminOrgs from './pages/admin/AdminOrgs';
import AdminClients from './pages/admin/AdminClients';
import AdminClientsAws from './pages/admin/AdminClientsAws';
import AdminClientOnboarding from './pages/admin/AdminClientOnboarding';
import AdminCommissioningAws from './pages/admin/AdminCommissioningAws';
import TOTPSetupWizard from './pages/TOTPSetupWizard';
import ChangePassword from './pages/ChangePassword';
import Documentation from './pages/Documentation';
import DigitalTwin from './pages/DigitalTwin';
import CgmEquipmentR1 from './pages/cgm/CgmEquipmentR1';
import CgmSectorsR1 from './pages/cgm/CgmSectorsR1';
import CgmDashboardLiveR1 from './pages/cgm/CgmDashboardLiveR1';

// CGM / SOVERDI validated UI pages
import CgmRuntimeLayout from './components/CgmRuntimeLayout';
import CgmSectorDetailR7 from './pages/cgm/CgmSectorDetailR7';
import SoverdiPumpingR7 from './pages/cgm/CgmPumpingR7';
import SoverdiEquipmentDetailR7 from './soverdi-clone/pages/SoverdiEquipmentDetailR7';
import SoverdiAlertsR7 from './soverdi-clone/pages/SoverdiAlertsR7';
import SoverdiAnalysisR7 from './soverdi-clone/pages/SoverdiAnalysisR7';
import SoverdiSettingsR7 from './soverdi-clone/pages/SoverdiSettingsR7';
import SoverdiDocumentationR7 from './soverdi-clone/pages/SoverdiDocumentationR7';
import SoverdiSupportR7 from './soverdi-clone/pages/SoverdiSupportR7';
import SoverdiContactR7 from './soverdi-clone/pages/SoverdiContactR7';

// Legacy R2/R6 contract markers retained for source-contract compatibility only.
// They are comments and are never executed; the live CGM route below uses CgmDashboardLiveR1.
// isCgmPilot ? cgmPage(<SoverdiDashboardR7 />) : <Dashboard />
// isCgmPilot ? <SoverdiDashboardR7 /> : <Dashboard />

// Layout
import Layout from './components/Layout';
import AccountBoundary from './components/AccountBoundary';
import CgmPageFrame from './components/CgmPageFrame';
import { AWS_ADMIN_COMMISSIONING_PATH, getAuthenticatedHomePath, IS_AWS_MODE, IS_DEV_DEPLOYMENT, isPathEnabled } from './config/frontend';
import { isCgmPilotExperience } from './config/cgmPilot';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const ModeRoute: React.FC<{ path: string; children: React.ReactNode }> = ({ path, children }) =>
  isPathEnabled(path) ? <>{children}</> : <Navigate to="/dashboard" replace />;

const RuntimeLayout: React.FC = () => {
  const { user } = useAuth();
  const isCgmPilot = IS_AWS_MODE && isCgmPilotExperience(user?.org_id, user?.role);
  return isCgmPilot ? (
    <CgmDisplayModeProvider>
      <CgmRuntimeLayout />
    </CgmDisplayModeProvider>
  ) : <Layout />;
};

const LegacySoverdiSettingsRedirect: React.FC = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: '/settings', search: location.search, hash: location.hash }}
      replace
    />
  );
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const isCgmPilot = IS_AWS_MODE && isCgmPilotExperience(user?.org_id, user?.role);
  const authenticatedHomePath = getAuthenticatedHomePath(user?.role);
  const isAwsSuperAdmin = IS_AWS_MODE && user?.role === 'superadmin';
  const fatRole = typeof user?.role === 'string' ? user.role.trim().toLowerCase() : '';
  const canUseFat = IS_AWS_MODE && IS_DEV_DEPLOYMENT && ['owner', 'admin', 'org_admin', 'coop_admin', 'farmer', 'farm_manager', 'technician'].includes(fatRole);
  const cgmPage = (page: React.ReactNode) => <CgmPageFrame>{page}</CgmPageFrame>;

  return (
    <Routes>
      <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to={authenticatedHomePath} replace />} />
      <Route path="/auth/totp-setup" element={<TOTPSetupWizard />} />
      <Route path="/auth/change-password" element={<ChangePassword />} />
      <Route path="/org/switch" element={<ProtectedRoute><ModeRoute path="/org/switch"><OrgSwitch /></ModeRoute></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><RuntimeLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to={authenticatedHomePath} replace />} />
        <Route
          path="dashboard"
          element={isAwsSuperAdmin ? <Navigate to="/admin" replace /> : isCgmPilot ? cgmPage(<CgmDashboardLiveR1 />) : <Dashboard />}
        />
        <Route
          path="sectors"
          element={<ModeRoute path="/sectors">{isCgmPilot ? cgmPage(<CgmSectorsR1 />) : <Navigate to="/dashboard" replace />}</ModeRoute>}
        />
        <Route
          path="sectors/:sectorId"
          element={<ModeRoute path="/sectors">{isCgmPilot ? cgmPage(<CgmSectorDetailR7 />) : <Navigate to="/dashboard" replace />}</ModeRoute>}
        />
        <Route
          path="pilotage"
          element={isCgmPilot ? <Navigate to="/sectors" replace /> : <Navigate to="/dashboard" replace />}
        />
        <Route
          path="pumping"
          element={<ModeRoute path="/pumping">{isCgmPilot ? cgmPage(<SoverdiPumpingR7 />) : <Navigate to="/dashboard" replace />}</ModeRoute>}
        />
        <Route path="fat" element={canUseFat ? <AgriSafeFatR1 /> : <Navigate to={authenticatedHomePath} replace />} />
        <Route path="map" element={<ModeRoute path="/map"><Map /></ModeRoute>} />
        <Route path="zones" element={<ModeRoute path="/zones"><Zones /></ModeRoute>} />
        <Route
          path="devices"
          element={isCgmPilot ? <CgmExpertRoute>{cgmPage(<CgmEquipmentR1 />)}</CgmExpertRoute> : <Devices />}
        />
        <Route
          path="r6-soverdi-equipment/:nodeId"
          element={isCgmPilot ? <CgmExpertRoute>{cgmPage(<SoverdiEquipmentDetailR7 />)}</CgmExpertRoute> : <Navigate to="/dashboard" replace />}
        />
        <Route path="alerts" element={isCgmPilot ? cgmPage(<SoverdiAlertsR7 />) : <Alerts />} />
        <Route path="schedules" element={<ModeRoute path="/schedules"><Schedules /></ModeRoute>} />
        <Route path="irrigation-journal" element={isCgmPilot ? cgmPage(<CgmIrrigationJournal />) : <Navigate to="/dashboard" replace />} />
        <Route path="programming" element={isCgmPilot ? cgmPage(<ProgrammingPage />) : <Navigate to="/dashboard" replace />} />
        <Route path="irrigation-history" element={isCgmPilot ? <CgmExpertRoute>{cgmPage(<SoverdiAnalysisR7 />)}</CgmExpertRoute> : <ModeRoute path="/irrigation-history"><IrrigationHistory /></ModeRoute>} />
        <Route path="iot" element={isCgmPilot ? <Navigate to="/sectors" replace /> : <IoTNodes />} />
        <Route path="digital-twin" element={<DigitalTwin />} />
        <Route path="work-orders" element={<ModeRoute path="/work-orders"><WorkOrders /></ModeRoute>} />
        <Route
          path="settings"
          element={isCgmPilot ? <CgmExpertRoute>{cgmPage(<SoverdiSettingsR7 />)}</CgmExpertRoute> : <ModeRoute path="/settings"><Settings /></ModeRoute>}
        />
        <Route path="documentation" element={isCgmPilot ? cgmPage(<SoverdiDocumentationR7 />) : <Documentation />} />
        <Route path="support" element={isCgmPilot ? cgmPage(<SoverdiSupportR7 />) : <Navigate to="/dashboard" replace />} />
        <Route path="contact" element={isCgmPilot ? cgmPage(<SoverdiContactR7 />) : <Navigate to="/dashboard" replace />} />

        {/* Compatibility aliases from the validated R6.3 UI. */}
        <Route path="farmer-dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="farmer-expert-dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="r6-soverdi-dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="r6-soverdi-sectors" element={<Navigate to="/sectors" replace />} />
        <Route path="r6-soverdi-pumping" element={<Navigate to="/pumping" replace />} />
        <Route path="r6-soverdi-equipment" element={<Navigate to="/devices" replace />} />
        <Route path="r6-soverdi-alerts" element={<Navigate to="/alerts" replace />} />
        <Route path="r6-soverdi-analysis" element={<Navigate to="/irrigation-history" replace />} />
        <Route path="r6-soverdi-settings" element={<LegacySoverdiSettingsRedirect />} />
        <Route path="r6-soverdi-documentation" element={<Navigate to="/documentation" replace />} />
        <Route path="r6-soverdi-support" element={<Navigate to="/support" replace />} />
        <Route path="r6-soverdi-contact" element={<Navigate to="/contact" replace />} />
        <Route path="devices/pumping/pump-station-1" element={<Navigate to="/pumping" replace />} />

        {/* Admin Routes */}
        <Route path="admin" element={<ModeRoute path="/admin">{IS_AWS_MODE ? <AdminDashboardAws /> : <AdminDashboard />}</ModeRoute>} />
        <Route path="admin/users" element={<ModeRoute path="/admin/users">{IS_AWS_MODE ? <AdminUsersAws /> : <AdminUsers />}</ModeRoute>} />
        <Route path="admin/orgs" element={<ModeRoute path="/admin/orgs"><AdminOrgs /></ModeRoute>} />
        <Route path="admin/clients" element={<ModeRoute path="/admin/clients">{IS_AWS_MODE ? <AdminClientsAws /> : <AdminClients />}</ModeRoute>} />
        <Route path="admin/clients/new" element={<ModeRoute path="/admin/clients/new"><AdminClientOnboarding /></ModeRoute>} />
        <Route
          path="admin/commissioning"
          element={<ModeRoute path={AWS_ADMIN_COMMISSIONING_PATH}>{isAwsSuperAdmin ? <AdminCommissioningAws /> : <Navigate to={authenticatedHomePath} replace />}</ModeRoute>}
        />
      </Route>
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <AccountBoundary>
              <IrrigationProvider>
                <Router>
                  <div className="App">
                    <AppRoutes />
                    <Toaster position="top-right" richColors />
                  </div>
                </Router>
              </IrrigationProvider>
            </AccountBoundary>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
